// Pure, React-free state-transition logic for the GenerateSetlist version
// stack. This module deliberately imports nothing — in particular no
// `react` — so it can be executed directly by Node (see
// `__tests__/setlistVersionBranching.check.ts`) without any build step.
//
// Session-scoped, in-memory version history for a GenerateSetlist song
// array. This module never persists anything — no localStorage,
// sessionStorage, IndexedDB, cookies, or network/GraphQL calls. History
// lives entirely in the caller's React state and dies with the component
// (SETVER-11). Lock state (`lockedTeamSongIds`) and key overrides
// (`songOverrides`) are explicitly out of scope per SETVER-09 and must
// never appear in a snapshot.
//
// SETVER-16 through SETVER-20 add an interaction-aware branching model:
// Generate with no interaction since the current top-level Setlist's last
// generate appends a brand-new Setlist entry at the end of the flattened
// history (never deleting anything); Generate after an interaction that
// is still "pending" (a slot-count change, or locked songs) splices a
// new Version entry in immediately after the pointer (also never
// deleting anything).
//
// A song switch or a finished reorder session is a deliberate, finished
// choice, not pending work: it finalizes immediately as its own new
// Version via `applyCommitEditVersion` and does NOT set the dirty mark,
// so a Generate right after (with no further edit) starts a fresh
// top-level Setlist instead of branching yet another Version on top.
//
// `dirtySetlistNumber: number | null` replaces a single global boolean. It
// holds the `setlistNumber` of the Setlist that has been interacted with
// since its own last Generate, or `null` when nothing is pending. It is
// set by `applyMarkDirty` at the one remaining edit-type interaction
// site (the song-count panel's Save) to the pointer entry's
// `setlistNumber`, and compared against the pointer entry's
// `setlistNumber` inside `applyCommitGenerate` so a dirty mark made
// under one Setlist can never route a Generate made under a different
// one (the WR-02 fix). It is cleared on every Generate and on save
// (`createVersionStackState`/`resetVersions`).
//
// Locked songs are handled separately from `dirtySetlistNumber`, not via
// `applyMarkDirty`: `applyCommitGenerate` also takes a live `hasActiveLocks`
// flag (whether any song is currently locked, evaluated at Generate time,
// not latched from the lock-toggle click). Locking is a standing
// constraint rather than a one-off edit — as long as at least one song
// stays locked, every Generate keeps branching a Version even across
// repeated Generates with no other edit between them. Dropping to zero
// locked songs removes the signal, so a subsequent otherwise-clean
// Generate goes back to appending a brand-new top-level Setlist.
//
// `applyCommitVersion`/`applyUndo`/`applyRedo`/`createVersionStackState`
// keep their exact Phase 83 truncate/collapse semantics (D-01).

export type SetlistVersionSnapshot = {
	selectedSongIds: string[];
	totalSongPositions: number;
	postSermonSongPosition?: number | null;
};

export type SetlistVersionEntry = {
	snapshot: SetlistVersionSnapshot;
	setlistNumber: number;
	versionNumber: number;
};

export type VersionStackState = {
	versions: SetlistVersionEntry[];
	pointer: number;
	// Replaces Plan 90-01's global boolean dirty flag. Holds the
	// setlistNumber of the Setlist that has been interacted with since its
	// own last Generate, or null when nothing is pending. D-02: "scoped to
	// the current top-level Setlist".
	dirtySetlistNumber: number | null;
};

export const areSnapshotsEqual = (
	a: SetlistVersionSnapshot,
	b: SetlistVersionSnapshot,
): boolean => {
	if (a.totalSongPositions !== b.totalSongPositions) {
		return false;
	}

	const aPostSermon = a.postSermonSongPosition ?? undefined;
	const bPostSermon = b.postSermonSongPosition ?? undefined;
	if (aPostSermon !== bPostSermon) {
		return false;
	}

	if (a.selectedSongIds.length !== b.selectedSongIds.length) {
		return false;
	}

	for (let i = 0; i < a.selectedSongIds.length; i++) {
		if (a.selectedSongIds[i] !== b.selectedSongIds[i]) {
			return false;
		}
	}

	return true;
};

export const cloneSnapshot = (
	snapshot: SetlistVersionSnapshot,
): SetlistVersionSnapshot => ({
	...snapshot,
	selectedSongIds: [...snapshot.selectedSongIds],
});

export const createVersionStackState = (
	snapshot: SetlistVersionSnapshot,
): VersionStackState => ({
	versions: [
		{
			snapshot: cloneSnapshot(snapshot),
			setlistNumber: 1,
			versionNumber: 1,
		},
	],
	pointer: 0,
	dirtySetlistNumber: null,
});

// CR-01 fix (label side): distinct finalized version numbers for the given
// setlistNumber, not raw entry count (which includes in-progress
// commitVersion edit duplicates carrying the same setlistNumber/
// versionNumber forward).
export const hasSiblingVersions = (
	versions: SetlistVersionEntry[],
	setlistNumber: number,
): boolean => {
	const distinctVersionNumbers = new Set<number>();
	for (const entry of versions) {
		if (entry.setlistNumber === setlistNumber) {
			distinctVersionNumbers.add(entry.versionNumber);
		}
	}
	return distinctVersionNumbers.size > 1;
};

// CR-01 fix (assignment side): the next versionNumber to assign is the
// largest versionNumber already used under this setlistNumber, plus one.
// A reduce (not filter(...).length) so an empty match yields 1, not
// negative Infinity.
export const nextVersionNumberFor = (
	versions: SetlistVersionEntry[],
	setlistNumber: number,
): number =>
	versions.reduce(
		(max, entry) =>
			entry.setlistNumber === setlistNumber && entry.versionNumber > max
				? entry.versionNumber
				: max,
		0,
	) + 1;

export const nextSetlistNumberFor = (
	versions: SetlistVersionEntry[],
): number =>
	versions.reduce(
		(max, entry) => (entry.setlistNumber > max ? entry.setlistNumber : max),
		0,
	) + 1;

export const deriveVersionLabel = (state: VersionStackState): string => {
	const currentEntry = state.versions[state.pointer];
	return hasSiblingVersions(state.versions, currentEntry.setlistNumber)
		? `${currentEntry.setlistNumber}.${currentEntry.versionNumber}`
		: `${currentEntry.setlistNumber}`;
};

// D-01: Phase 83's exact truncate-then-append semantics, unchanged.
export const applyCommitVersion = (
	prev: VersionStackState,
	snapshot: SetlistVersionSnapshot,
): VersionStackState => {
	const pointerEntry = prev.versions[prev.pointer];
	if (areSnapshotsEqual(pointerEntry.snapshot, snapshot)) {
		return prev;
	}

	const truncated = prev.versions.slice(0, prev.pointer + 1);
	const nextVersions = [
		...truncated,
		{
			snapshot: cloneSnapshot(snapshot),
			setlistNumber: pointerEntry.setlistNumber,
			versionNumber: pointerEntry.versionNumber,
		},
	];

	return {
		...prev,
		versions: nextVersions,
		pointer: nextVersions.length - 1,
	};
};

// Some manual edits are a deliberate, finished choice rather than an
// in-progress step toward a future Generate — a song switch, or a reorder
// session ending on "Done". These finalize immediately as their own new,
// distinctly-numbered Version under the current Setlist (instead of
// sharing the pointer entry's versionNumber the way applyCommitVersion's
// in-progress checkpoints do), so the S#.V# label advances right away
// without waiting for the next Generate. Because they behave like their
// own mini-Generate (a real, distinctly-numbered Version), they splice
// non-destructively after the pointer, exactly like applyCommitGenerate's
// version-branch: if the pointer isn't at the end of history (e.g. the
// user undid back into an earlier Setlist before editing), any Setlists
// or Versions ahead of the pointer are preserved and shifted right, never
// deleted. This differs from applyCommitVersion's truncate-on-edit
// behavior (D-01, kept for the remaining in-progress-checkpoint call
// sites), since a switch/reorder-Done result is a first-class Version a
// user may still want to come back to, not a discarded intermediate step.
export const applyCommitEditVersion = (
	prev: VersionStackState,
	snapshot: SetlistVersionSnapshot,
): VersionStackState => {
	const pointerEntry = prev.versions[prev.pointer];
	if (areSnapshotsEqual(pointerEntry.snapshot, snapshot)) {
		return prev;
	}

	const setlistNumber = pointerEntry.setlistNumber;
	const newEntry: SetlistVersionEntry = {
		snapshot: cloneSnapshot(snapshot),
		setlistNumber,
		versionNumber: nextVersionNumberFor(prev.versions, setlistNumber),
	};
	const nextVersions = [
		...prev.versions.slice(0, prev.pointer + 1),
		newEntry,
		...prev.versions.slice(prev.pointer + 1),
	];

	return {
		...prev,
		versions: nextVersions,
		pointer: prev.pointer + 1,
	};
};

// WR-02 fix: scope the dirty mark to the pointer entry's setlistNumber.
export const applyMarkDirty = (prev: VersionStackState): VersionStackState => {
	const pointerSetlistNumber = prev.versions[prev.pointer].setlistNumber;
	if (prev.dirtySetlistNumber === pointerSetlistNumber) {
		return prev;
	}
	return { ...prev, dirtySetlistNumber: pointerSetlistNumber };
};

export const applyCommitGenerate = (
	prev: VersionStackState,
	snapshot: SetlistVersionSnapshot,
	hasActiveLocks: boolean,
): VersionStackState => {
	const pointerEntry = prev.versions[prev.pointer];

	if (areSnapshotsEqual(pointerEntry.snapshot, snapshot)) {
		return { ...prev, dirtySetlistNumber: null };
	}

	// Locked songs are a standing constraint, not a one-off edit event: as
	// long as at least one song is locked at Generate time, every Generate
	// keeps branching a new Version under the current Setlist, even across
	// repeated Generates with no other edit in between. Dropping to zero
	// locked songs removes this signal — from there, an otherwise-clean
	// Generate goes back to appending a brand-new top-level Setlist.
	const isDirty =
		prev.dirtySetlistNumber === pointerEntry.setlistNumber || hasActiveLocks;

	if (!isDirty) {
		const newEntry: SetlistVersionEntry = {
			snapshot: cloneSnapshot(snapshot),
			setlistNumber: nextSetlistNumberFor(prev.versions),
			versionNumber: 1,
		};
		const nextVersions = [...prev.versions, newEntry];

		return {
			...prev,
			versions: nextVersions,
			pointer: nextVersions.length - 1,
			dirtySetlistNumber: null,
		};
	}

	const setlistNumber = pointerEntry.setlistNumber;
	const newEntry: SetlistVersionEntry = {
		snapshot: cloneSnapshot(snapshot),
		setlistNumber,
		versionNumber: nextVersionNumberFor(prev.versions, setlistNumber),
	};
	const nextVersions = [
		...prev.versions.slice(0, prev.pointer + 1),
		newEntry,
		...prev.versions.slice(prev.pointer + 1),
	];

	return {
		...prev,
		versions: nextVersions,
		pointer: prev.pointer + 1,
		dirtySetlistNumber: null,
	};
};

// Deliberately does not touch dirtySetlistNumber (Scenario D pins this):
// D-02 only specifies that undo/redo don't set the dirty mark, and the
// chosen semantics keep it as-is so that a real interaction under the
// current Setlist (before the Undo) is still honoured by the next
// Generate.
export const applyUndo = (prev: VersionStackState): VersionStackState => {
	if (prev.pointer <= 0) {
		return prev;
	}
	return { ...prev, pointer: prev.pointer - 1 };
};

export const applyRedo = (prev: VersionStackState): VersionStackState => {
	if (prev.pointer >= prev.versions.length - 1) {
		return prev;
	}
	return { ...prev, pointer: prev.pointer + 1 };
};
